import React, { useEffect, useState } from "react";
import { Layout, Menu, Drawer, Button, Grid } from "antd";
import {
  UserOutlined,
  MenuOutlined,
  AppstoreAddOutlined,
  PlaySquareOutlined,
  EnvironmentOutlined,
  FundProjectionScreenOutlined,
  UsergroupAddOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  DesktopOutlined,
  DashboardOutlined,
  BookOutlined,
  NotificationOutlined,
  AppstoreOutlined,
  TagsOutlined,
  HddOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";

const { Sider, Header } = Layout;
const { useBreakpoint } = Grid;

const AppSidebar = () => {
  const screens = useBreakpoint();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [userPermissions, setUserPermissions] = useState({});

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    setUserPermissions(userData.permissions || {});
  }, []);

  const menuItems = [
    {
      key: "home",
      label: "Home",
      permissions: [
        "locations.view",
        "users.view",
        "roles.view",
        "cities.view",
      ],
      type: "group",
    },
    {
      key: "dashboard",
      icon: <DashboardOutlined />,
      permissions: [
        "signage.media_view",
        "signage.playlist_view",
        "signage.screens_view"
      ],
      label: "Dashboard",
    },
    {
      key: "masters",
      label: "Masters",
      permissions: [
        "locations.view",
        "users.view",
        "roles.view",
        "cities.view",
      ],
      type: "group",
    },
    {
      key: "roles",
      icon: <UsergroupAddOutlined />,
      permissions: ["roles.view"],
      label: "Roles",
    },
    {
      key: "users",
      icon: <UserOutlined />,
      permissions: ["users.view"],
      label: "Users",
    },
    {
      key: "cities",
      icon: <GlobalOutlined />,
      permissions: ["cities.view"],
      label: "Cities",
    },
    {
      key: "locations",
      icon: <EnvironmentOutlined />,
      permissions: ["locations.view"],
      label: "Locations",
    },
    {
      key: "brands",
      icon: <TagsOutlined />,
      permissions: ["brands.view"],
      label: "Brands",
    },
    {
      key: "app",
      label: "App",
      permissions: [
        "signage.media_view",
        "signage.playlist_view",
        "signage.screens_view",
      ],
      type: "group",
    },
    {
      key: "library",
      icon: <AppstoreAddOutlined />,
      permissions: ["signage.media_view"],
      label: "Media Library",
    },
    {
      key: "playlists",
      icon: <PlaySquareOutlined />,
      permissions: ["signage.playlist_view"],
      label: "Playlists",
    },
    {
      key: "screens",
      icon: <FundProjectionScreenOutlined />,
      permissions: ["signage.screens_view"],
      label: "Screens",
    },
    {
      key: "schedules",
      icon: <ClockCircleOutlined />,
      permissions: ["signage.schedule_view"],
      label: "Schedules",
    },
    {
      key: "campaigns",
      icon: <NotificationOutlined />,
      permissions: ["signage.campaigns_view"],
      label: "Campaigns",
    },
    {
      key: "logs",
      icon: <BookOutlined />,
      permissions: ["signage.logs_view"],
      label: "Logs",
    },
    {
      key: "inventory",
      icon: <HddOutlined />,
      permissions: ["signage.inventory_view"],
      label: "Inventory",
    },
    {
      key: "devices-group",
      icon: <DesktopOutlined />,
      label: "Devices",
      children: [
        {
          key: "signage-tvs",
          permissions: ["signage.devices_view"],
          label: "Signage TVs",
        },
        {
          key: "device-errors",
          permissions: ["signage.device_errors_view"],
          label: "Device Errors",
        },
        {
          key: "meeting-room-tablets",
          permissions: ["signage.meeting_room_view"],
          label: "Meeting Room Tablets",
        },
      ],
    },
    {
      key: "apps",
      icon: <AppstoreOutlined />,
      label: "Apps",
      children: [
        {
          key: "android-app-versions",
          permissions: ["signage.android_app_version_view"],
          label: "Android TV App",
        },
        {
          key: "tizen-app-versions",
          permissions: ["signage.tizen_app_version_view"],
          label: "Tizen App",
        },
        {
          key: "lg-app-versions",
          permissions: ["signage.lg_app_version_view"],
          label: "LG App",
        },
        {
          key: "web-app-versions",
          permissions: ["signage.web_app_version_view"],
          label: "Web App",
        },
        {
          key: "meeting-room-app",
          permissions: ["signage.meeting_room_view"],
          label: "Meeting Room App",
        },
      ],
    },
  ];

  const handleMenuClick = ({ key }) => {
    navigate(`/${key}`);
    setDrawerVisible(false);
  };

  const hasPermission = (item) =>
    !item.permissions ||
    item.permissions.some((perm) => userPermissions[perm] === "1");

  // Submenus (e.g. "Apps") have their children filtered the same way -
  // the parent itself is dropped if every child ends up hidden, so an
  // empty "Apps" group never shows.
  const filteredMenuItems = menuItems
    .map((item) => {
      if (item.children) {
        const children = item.children.filter(hasPermission);
        return children.length > 0 ? { ...item, children } : null;
      }
      return hasPermission(item) ? item : null;
    })
    .filter(Boolean);

  const menu = (
    <>
      <style>{`
        .ant-menu-submenu .ant-menu-item {
          font-size: 12px;
        }
      `}</style>
      <Menu
        mode="inline"
        selectedKeys={[location.pathname.split("/")[1]]}
        defaultOpenKeys={["apps", "devices-group"]}
        items={filteredMenuItems}
        onClick={handleMenuClick}
      />
    </>
  );

  return !screens.md ? (
    <>
      <Header style={{ padding: "0 1rem" }} className="bg-light">
        <Button
          icon={<MenuOutlined />}
          onClick={() => setDrawerVisible(true)}
        />
      </Header>
      <Drawer
        title="Menu"
        placement="left"
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        styles={{ body: { padding: 0 } }}
      >
        {menu}
      </Drawer>
    </>
  ) : (
    <Sider width={200} className="site-layout-background bg-white">
      {menu}
    </Sider>
  );
};

export default AppSidebar;
